import React from 'react';
import { useQuery } from 'react-query';
import * as env from 'config/environment';
import * as consts from 'config/consts';

import { getReport, ReportPositionRanking, ReportArticle } from 'config/api';
import { formatDate } from 'utils/formatter';
import { useScroll } from 'hooks/useScroll';
import { Frequency, reportLoadingStyle } from 'pages/NewReport';
import Loading from 'components/commons/loading/Loading';
import { isPremium } from 'components/features/utils/userStatus';
import { getDate } from 'components/features/utils/converter';
import { LineNoticeBanner } from 'components/features/home/HomeContentsInternal';
import { AssetChange } from './components/AssetChange';
import { AssetChangePerAccount } from './components/AssetChangePerAccount';
import { ComparisonBetweenIndexAndAsset } from './components/ComparisonBetweenIndexAndAsset';
import { ProfitAndLossValuationRanking } from './components/ProfitAndLossValuationRanking';
import { ManagedAssetsReport } from './components/ManagedAssetsReport';
import { AssetTransition } from './components/AssetTransition';
import { yyyymmddToDate } from '../daily-report/DailyReport';

export type ReportPeriodProps = {
  from: number;
  to: number;
};

export type IsLinkedLineProps = {
  isLinkedLine: boolean;
};

// 実装

// MEMO: 負債対応の本番リリースを行った日。リリースの影響で不正なデータが作成されるが、リカバリ不可のため例外対応を行う。
export const day = 20231101;
export const ItemMaskCheck = (from: number, to: number) => {
  const startDay = getDate(from);
  const endDay = getDate(to);
  const baseDay = getDate(day);
  return startDay <= baseDay && baseDay <= endDay;
};

const APIFail: React.FC<Record<string, unknown>> = () => {
  return (
    <>
      <div className="bg-white">
        <div className="flex flex-col items-center justify-start py-8 px-6">
          <div className="mb-6 text-lg text-center font-bold text-bluegray-700">
            準備中です
          </div>
          <img src="/static/images/report/report_stand-by.svg" alt="" />
          <div className="w-full mt-6 mb-10 mx-auto text-left text-xs text-black">
            週次レポートは毎週土曜日に配信されます。
            <br />
            初回レポートが作成されるまでもうしばらくお待ち下さい。
          </div>
          <a id="weeklyreport_line" href={env.lineAddFriendURL}>
            <img
              src="/static/images/line1.png"
              alt="友だち追加"
              className="h-12.5"
              style={{ width: '265px' }}
            />
          </a>
        </div>
      </div>
    </>
  );
};

export const getQueryPeriod = (
  from: number,
  to: number
): { from: string; to: string } => {
  const queryFrom = formatDate(yyyymmddToDate(from.toString()), 'yyyy/MM/dd');
  const queryTo = formatDate(yyyymmddToDate(to.toString()), 'yyyy/MM/dd');
  return { from: queryFrom, to: queryTo };
};

export type ReportBaseProps = {
  From: number;
  To: number;
  TotalAssetAmount: number | null;
  PreviousTotalAssetAmount: number | null;
  DiffTotalAssetAmount: number | null;
  DiffTotalAssetRate: number | null;
  DiffStockRate: number | null;
  DiffMarginRate: number | null;
  DiffMarginProfitOrLossRate: number | null;
  DiffRoboAdvisorRate: number | null;
  DiffUSStockRate: number | null;
  DiffInvestmentTrustRate: number | null;
  DiffUnlistedStockRate: number | null;
  DiffEtfEtnRate: number | null;
  DiffForeignEtfEtnRate: number | null;
  DiffReitRate: number | null;
  DiffForeignReitRate: number | null;
  DiffBondRate: number | null;
  DiffForeignBondRate: number | null;
  DiffEbRate: number | null;
  DiffForeignEbRate: number | null;
  DiffRealEstateRate: number | null;
  DiffCashRate: number | null;
  DiffOtherRate: number | null;
  AssetFluctuationThresholdPer?: number | null;
};

type ReportResult = {
  CreatedAt: string;
  PreviousQuery: string;
  NextQuery: string;
  Base: ReportBaseProps;
  Articles: ReportArticle[];
  PositionRanking: ReportPositionRanking;
};

type ReportResultProps = {
  report: ReportResult;
};
const WeeklyReportInternal = (
  props: IsLinkedLineProps &
    ReportResultProps & {
      userName: string;
      setQuery: (q: string, frequency: Frequency) => void;
    }
) => {
  const { isLinkedLine, userName, setQuery, report } = props;
  useScroll();

  const from = report.Base.From !== undefined ? report.Base.From : 0;
  const to = report.Base.To !== undefined ? report.Base.To : 0;
  const isItemMask = ItemMaskCheck(from, to);

  const base_ = report.Base as ReportBaseProps;
  const positionRanking = report.PositionRanking;

  const queryPeriod = getQueryPeriod(base_.From, base_.To);

  return (
    <>
      <div className="">
        <div className="bg-white">
          <AssetChange
            {...{
              Base: base_,
              CreatedAt: report.CreatedAt,
              NextQuery: report.NextQuery,
              PreviousQuery: report.PreviousQuery,
              setQuery,
              isItemMask,
            }}
          />
          {!isItemMask && (
            <>
              <AssetTransition {...{ Base: base_, userName }} />
              {!isLinkedLine && isPremium() && (
                <LineNoticeBanner
                  {...{ className: ['mb-2', 'w-1/2', 'w-3/4'] }}
                />
              )}
              <AssetChangePerAccount {...queryPeriod} />
            </>
          )}
        </div>
        <div className="">
          <ManagedAssetsReport
            ComparisonBetweenIndexAndAsset={() => (
              <ComparisonBetweenIndexAndAsset {...queryPeriod} />
            )}
            ProfitAndLossInformation={() => (
              <ProfitAndLossValuationRanking
                {...{ PositionRanking: positionRanking }}
              />
            )}
            isContentsEmpty={false}
          />
        </div>
      </div>
    </>
  );
};

export const WeeklyReport: React.FC<IsLinkedLineProps & {
  userName: string;
  query: string | null;
  setQuery: (q: string, frequency: Frequency) => void;
}> = props => {
  const query = props.query ?? undefined;
  const { data: reportData, isLoading, isRefetching } = useQuery(
    'weeklyReport',
    () => fetchReport(query),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (reportData === null || reportData === undefined)
    return <Loading style={reportLoadingStyle} loading={true} />;

  if (reportData.Week === null) return <APIFail />;

  if (
    isLoading ||
    isRefetching ||
    reportData.Week.PositionRanking === null ||
    reportData.Week.PositionRanking === undefined ||
    reportData.Week.Base.From === null ||
    reportData.Week.Base.To === null ||
    reportData.Week.NextQuery === undefined ||
    reportData.Week.PreviousQuery === undefined
  )
    return <Loading style={reportLoadingStyle} loading={true} />;

  return (
    <WeeklyReportInternal
      {...{
        report: reportData.Week as ReportResult,
        isLinkedLine: props.isLinkedLine,
        userName: props.userName,
        setQuery: props.setQuery,
      }}
    />
  );
};

const fetchReport = async (query: string | undefined) => {
  return await getReport(consts.reportType.週次, query);
};
